import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import api from "@/lib/axios"
import { getListParentKomponenPenilaian } from "@/services/getData"
import { KomponenPenilaian, Option, ParentKomponenPenilaian } from "@/types"
import { AxiosError } from "axios"
import { Loader2 } from "lucide-react"
import { useState } from "react"
import toast from "react-hot-toast"
import { FaPlus } from "react-icons/fa6"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

const postData = async (newData: KomponenPenilaian) => {
  const response = await api.post(
    "/operator-pasca/komponen-penilaian/komponen-store",
    newData
  )
  return response.data
}

const patchData = async (newData: KomponenPenilaian) => {
  const response = await api.patch(
    "/operator-pasca/komponen-penilaian/komponen-update",
    newData
  )
  return response.data
}

const deleteData = async (deletedData: {
  id_ref_komponen_penilaian_pasca: string
}) => {
  const response = await api({
    url: "/operator-pasca/komponen-penilaian/komponen-delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: KomponenPenilaian | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<KomponenPenilaian>({
    id_ref_komponen_penilaian_pasca:
      selectedRow?.id_ref_komponen_penilaian_pasca || "",
    id_ref_parent_komponen_penilaian_pasca:
      selectedRow?.id_ref_parent_komponen_penilaian_pasca || "",
    nama_komponen: selectedRow?.nama_komponen || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const { data: listParentKomponen } = useQuery({
    queryKey: ["listParentKomponenPenilaian"],
    queryFn: getListParentKomponenPenilaian,
    select: (data) => {
      return data?.map((item: ParentKomponenPenilaian) => {
        return {
          label: `${item.kode}-${item.nama_ref_parent_komponen}`,
          value: `${item.id_ref_parent_komponen_penilaian_pasca}`,
        }
      })
    },
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listKomponenPenilaian"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete "
          description={`Apakah anda yakin akan menghapus "${""}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data" : "Update Data"}
      />

      <div>
        <Label>Parent Komponen Penilaian</Label>
        <Select
          value={form.id_ref_parent_komponen_penilaian_pasca || ""}
          onValueChange={(value: string) => {
            setForm((prev) => ({
              ...prev,
              id_ref_parent_komponen_penilaian_pasca: `${value}`,
            }))
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={"Pilih Parent Komponen"}>
              {form.id_ref_parent_komponen_penilaian_pasca &&
                listParentKomponen?.find((option: Option) => {
                  return (
                    option.value == form.id_ref_parent_komponen_penilaian_pasca
                  )
                })?.label}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {listParentKomponen?.map((option: Option, index: number) => (
              <SelectItem key={index} value={`${option.value}`}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Nama Komponen Penilaian</Label>
        <Input
          placeholder="Ketik nama komponen..."
          value={form.nama_komponen}
          onChange={(event) => {
            setForm((prev) => ({ ...prev, nama_komponen: event.target.value }))
          }}
        />
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
